import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { createTpInstructorUser, db } from "../../config/firebase";
import { joinName } from "../../utils/name";
import {
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  Typography,
  Divider,
} from "@mui/material";
import ModalDialogTitle from "../common/ModalDialogTitle";
import ModalActions from "../common/ModalActions";

// Dedicated "Add User" form for the TpInstructors page only. It carries the
// same base fields/user-type checkboxes as the shared TpAddUser form, plus a
// set of instructor-only fields (Ext, Notes, SCC ID, Sinclair Email, and the
// eLearn/Dental/PLTW/Academic instructor flags) that should never appear on
// the Add form for Staff/Coordinators/Admins.
const AddTpInstructor = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [ext, setExt] = useState("");
  const [password, setPassword] = useState("");
  const [sccId, setSccId] = useState("");
  const [sinclairEmail, setSinclairEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [eLearnInstructor, setELearnInstructor] = useState(false);
  const [dentalInstructor, setDentalInstructor] = useState(false);
  const [pltwInstructor, setPltwInstructor] = useState(false);
  const [academicInstructor, setAcademicInstructor] = useState(false);
  const [userTypes, setUserTypes] = useState({
    staff: false,
    coordinator: false,
    instructor: true,
    admin: false,
    pathwayManager: false,
  });
  const [partner, setPartner] = useState("");
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");

  // Coordinators are tied to a Tech Prep Partner org, so the dropdown only
  // offers partners currently marked active in the Partners collection.
  useEffect(() => {
    const loadPartners = async () => {
      try {
        const snap = await getDocs(collection(db, "TpPartners"));
        const names = snap.docs
          .map((d) => d.data())
          .filter((p) => p.Active_yn === "y" && p.Tech_Prep_Partner)
          .map((p) => p.Tech_Prep_Partner)
          .sort((a, b) => a.localeCompare(b));
        setPartnerOptions(names);
      } catch (e) {
        console.error("Error loading partners:", e);
        setPartnerOptions([]);
      }
    };
    loadPartners();
  }, []);

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, "");
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6,
      )}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhoneNumber(e.target.value));
  };

  const handleUserTypeChange = (type) => (e) => {
    setUserTypes((prev) => ({ ...prev, [type]: e.target.checked }));
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setExt("");
    setPassword("");
    setSccId("");
    setSinclairEmail("");
    setNotes("");
    setELearnInstructor(false);
    setDentalInstructor(false);
    setPltwInstructor(false);
    setAcademicInstructor(false);
    setUserTypes({
      staff: false,
      coordinator: false,
      instructor: true,
      admin: false,
      pathwayManager: false,
    });
    setPartner("");
  };

  const handleOpen = () => {
    setError("");
    setOpen(true);
  };

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    const userData = {
      firstName,
      lastName,
      name: joinName(firstName, lastName),
      phone,
      ext,
      notes,
      SccID: sccId,
      sinclair_email: sinclairEmail,
      eLearn_instructor: eLearnInstructor,
      dental_instructor: dentalInstructor,
      pltw_instructor: pltwInstructor,
      academic_instructor: academicInstructor,
      userTypes,
      techPrepAdminFlag: !!userTypes.admin,
      tp_partner: userTypes.coordinator ? partner : "",
      avatar: "",
    };

    try {
      const result = await createTpInstructorUser(email, password, userData);

      if (result.success) {
        setSnackbarMessage("Instructor added successfully");
        resetForm();
        setOpen(false);
        if (onSuccess) onSuccess("Instructor added successfully");
      } else {
        console.error("Instructor creation failed:", result.message);
        setError(result.message);
        setSnackbarMessage(result.message);
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error during instructor creation:", err);
      const errorMessage = err.message || "Error adding instructor";
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Add User
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>Add Instructor</ModalDialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            fullWidth
            margin="dense"
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            margin="dense"
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={handlePhoneChange}
            fullWidth
            margin="dense"
            placeholder="(xxx) xxx-xxxx"
            inputProps={{ maxLength: 14 }}
          />
          <TextField
            label="Ext"
            value={ext}
            onChange={(e) => setExt(e.target.value)}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            margin="dense"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={userTypes.staff}
                onChange={handleUserTypeChange("staff")}
              />
            }
            label="Staff"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={userTypes.coordinator}
                onChange={handleUserTypeChange("coordinator")}
              />
            }
            label="Coordinator"
          />
          {userTypes.coordinator && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Partner</InputLabel>
              <Select
                value={partner}
                onChange={(e) => setPartner(e.target.value)}
                label="Partner"
              >
                {partnerOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={userTypes.instructor}
                onChange={handleUserTypeChange("instructor")}
              />
            }
            label="Instructor"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={userTypes.admin}
                onChange={handleUserTypeChange("admin")}
              />
            }
            label="Admin"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={userTypes.pathwayManager}
                onChange={handleUserTypeChange("pathwayManager")}
              />
            }
            label="Pathway Manager"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Instructor Details
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={eLearnInstructor}
                onChange={(e) => setELearnInstructor(e.target.checked)}
              />
            }
            label="eLearn Instructor"
          />
          <FormControlLabel
            control={
              <Switch
                checked={dentalInstructor}
                onChange={(e) => setDentalInstructor(e.target.checked)}
              />
            }
            label="Dental Instructor"
          />
          <FormControlLabel
            control={
              <Switch
                checked={pltwInstructor}
                onChange={(e) => setPltwInstructor(e.target.checked)}
              />
            }
            label="PLTW Instructor"
          />
          <FormControlLabel
            control={
              <Switch
                checked={academicInstructor}
                onChange={(e) => setAcademicInstructor(e.target.checked)}
              />
            }
            label="Academic Instructor"
          />

          <TextField
            label="SCC ID"
            value={sccId}
            onChange={(e) => setSccId(e.target.value)}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Sinclair Email"
            type="email"
            value={sinclairEmail}
            onChange={(e) => setSinclairEmail(e.target.value)}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            margin="dense"
            multiline
            rows={3}
          />

          {loading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          saveLabel="Add Instructor"
        />
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddTpInstructor;
